import { Button, Input, Modal } from "antd";
import {
  Cake,
  Calendar,
  CreditCard,
  Pencil,
  Plus,
  Tag as TagIcon,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import type { OpsMemberDetailVO } from "@/api/member/type";
import { DetailRow, EmptyBlock } from "@/components/DetailBlocks";

/** 档案 Tab props */
export interface ProfileTabProps {
  detail: OpsMemberDetailVO;
  /** 备注保存中 */
  remarkSaving: boolean;
  /** 标签保存中 */
  tagSaving: boolean;
  /** 保存备注，返回是否成功 */
  onSaveRemark: (remark: string) => Promise<boolean>;
  /** 追加标签 */
  onAddTag: (tag: string) => Promise<boolean>;
  /** 移除标签 */
  onRemoveTag: (tag: string) => Promise<boolean>;
}

/**
 * 会员详情 - 档案 Tab：基本信息 / 标签 / 备注
 */
export const ProfileTab = ({
  detail,
  remarkSaving,
  tagSaving,
  onSaveRemark,
  onAddTag,
  onRemoveTag,
}: ProfileTabProps) => {
  // 是否处于备注编辑态
  const [editingRemark, setEditingRemark] = useState(false);
  // 备注草稿
  const [remarkDraft, setRemarkDraft] = useState(detail.remark ?? "");
  // 是否打开添加标签弹窗
  const [addTagOpen, setAddTagOpen] = useState(false);
  // 新标签草稿
  const [newTag, setNewTag] = useState("");

  // 详情备注变化时同步草稿（非编辑态）
  useEffect(() => {
    if (editingRemark) return;
    setRemarkDraft(detail.remark ?? "");
  }, [detail.remark, editingRemark]);

  // 会员标签列表
  const tags = detail.memberTags ?? [];

  /** 提交备注修改 */
  const handleSaveRemark = async () => {
    const ok = await onSaveRemark(remarkDraft.trim());
    if (ok) setEditingRemark(false);
  };

  /** 提交新增标签 */
  const handleConfirmAddTag = async () => {
    const tag = newTag.trim();
    if (!tag) return;
    const ok = await onAddTag(tag);
    // 成功后关闭弹窗并清空输入
    if (ok) {
      setAddTagOpen(false);
      setNewTag("");
    }
  };

  return (
    <div className="space-y-3">
      {/* 基本信息 */}
      <div className="rounded-lg bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]">
        <h4 className="text-sm font-medium text-slate-800 m-0 mb-3">基本信息</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <DetailRow
            icon={User}
            label="性别"
            value={detail.genderLabel || detail.gender || "-"}
            labelClassName="w-14"
          />
          <DetailRow
            icon={Cake}
            label="生日"
            value={detail.birthday || "-"}
            labelClassName="w-14"
          />
          <DetailRow
            icon={CreditCard}
            label="卡号"
            value={detail.memberCardNo || "-"}
            labelClassName="w-14"
          />
          <DetailRow
            icon={UserPlus}
            label="邀请人"
            value={detail.inviterName || "-"}
            labelClassName="w-14"
          />
          <DetailRow
            icon={Calendar}
            label="注册时间"
            value={detail.registerAt || "-"}
            labelClassName="w-14"
          />
          <DetailRow
            icon={Calendar}
            label="最近到店"
            value={detail.lastVisitAt || "-"}
            labelClassName="w-14"
          />
        </div>
      </div>

      {/* 会员标签 */}
      <div className="rounded-lg bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-medium text-slate-800 m-0">会员标签</h4>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 text-xs text-orange-500 hover:text-orange-600"
            onClick={() => {
              setNewTag("");
              setAddTagOpen(true);
            }}
          >
            <Plus className="w-3 h-3" />
            添加
          </button>
        </div>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-700"
              >
                <TagIcon className="w-3 h-3 text-slate-400 shrink-0" />
                {tag}
                <button
                  type="button"
                  disabled={tagSaving}
                  className="inline-flex text-slate-400 hover:text-slate-700 disabled:opacity-50"
                  aria-label={`移除标签 ${tag}`}
                  onClick={() => void onRemoveTag(tag)}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <EmptyBlock text="暂无标签" />
        )}
      </div>

      {/* 备注 */}
      <div className="rounded-lg bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-medium text-slate-800 m-0">备注</h4>
          {!editingRemark && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600"
              onClick={() => setEditingRemark(true)}
            >
              <Pencil className="w-3 h-3" />
              修改
            </button>
          )}
        </div>
        {editingRemark ? (
          <div className="space-y-2">
            <Input.TextArea
              rows={3}
              value={remarkDraft}
              maxLength={200}
              showCount
              placeholder="请输入备注内容"
              onChange={(e) => setRemarkDraft(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                size="small"
                onClick={() => {
                  setRemarkDraft(detail.remark ?? "");
                  setEditingRemark(false);
                }}
              >
                取消
              </Button>
              <Button type="primary" size="small" loading={remarkSaving} onClick={handleSaveRemark}>
                保存
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 whitespace-pre-wrap wrap-break-word m-0">
            {detail.remark?.trim() || "暂无备注"}
          </p>
        )}
      </div>

      <Modal
        title="添加标签"
        open={addTagOpen}
        confirmLoading={tagSaving}
        okText="确定"
        cancelText="取消"
        onOk={() => void handleConfirmAddTag()}
        onCancel={() => {
          setAddTagOpen(false);
          setNewTag("");
        }}
        destroyOnHidden
      >
        <Input
          value={newTag}
          maxLength={20}
          placeholder="请输入标签名称"
          onChange={(e) => setNewTag(e.target.value)}
          onPressEnter={() => void handleConfirmAddTag()}
        />
      </Modal>
    </div>
  );
};
